// Explainers tab: short psychoeducation pieces (written + spoken versions)
// the guide can use once it has formulated the user's pattern.

import type { Explainer, TalkTrackContent } from "../../types/talkTrack";
import { TextField } from "./fields";
import { nextUnitId } from "./format";
import { UnitCard } from "./UnitCard";
import { useUnitContext } from "./unitContext";

export function ExplainersEditor({
  topicId,
  content,
  onChange,
}: {
  topicId: string;
  content: TalkTrackContent;
  onChange: (next: TalkTrackContent) => void;
}) {
  const { editable } = useUnitContext();
  const setExplainers = (explainers: Explainer[]) => onChange({ ...content, explainers });
  const patch = (i: number, p: Partial<Explainer>) =>
    setExplainers(content.explainers.map((e, j) => (j === i ? { ...e, ...p } : e)));

  function add() {
    const id = nextUnitId(content, `${topicId}.explain.`, content.explainers.length + 1);
    setExplainers([
      ...content.explainers,
      { id, concept: "", title: "", audience: "general", text: "", voiceText: "", checkIn: "" },
    ]);
  }

  return (
    <>
      {content.explainers.length === 0 && (
        <div className="state-box">No explainers yet{editable ? " — add one below." : "."}</div>
      )}
      {content.explainers.map((e, i) => (
        <UnitCard key={e.id} unitId={e.id} kind="Explainer" title={e.title}
          onApplySuggestion={(text) => patch(i, { text })}
          onRemove={() => setExplainers(content.explainers.filter((_, j) => j !== i))}>
          <div className="row-3">
            <TextField label="Title" editable={editable} value={e.title} onChange={(title) => patch(i, { title })} />
            <TextField label="Concept" editable={editable} value={e.concept}
              onChange={(concept) => patch(i, { concept })} />
            <TextField label="Audience" editable={editable} value={e.audience} placeholder="general"
              onChange={(audience) => patch(i, { audience })} />
          </div>
          <TextField label="Wording (what the guide uses)" editable={editable} multiline rows={4} value={e.text}
            onChange={(text) => patch(i, { text })} />
          <TextField label="AI short version (reference only — the guide doesn't use it)" editable={editable}
            multiline rows={3} value={e.voiceText} onChange={(voiceText) => patch(i, { voiceText })} />
          <TextField label="Check-in after explaining" editable={editable} value={e.checkIn}
            onChange={(checkIn) => patch(i, { checkIn })} />
        </UnitCard>
      ))}
      {editable && <button className="btn" onClick={add}>+ Explainer</button>}
    </>
  );
}
